import AspectRatioSelect, { storyboardRatio, mediaAspectClass, videoClass } from "./AspectRatioSelect"
import React, { useCallback, useEffect, useRef, useState } from "react"
import { Button, Input, Select, Popconfirm } from "antd"
import { MdArrowBack, MdDownload, MdDelete, MdPlayCircle, MdChevronRight } from "react-icons/md"
import { videoDurations } from "./aiTools"
import RenewableVideo from "./RenewableVideo"
import { downloadMedia } from "./mediaRenewal"
import { getVideoAccess, listVideoFrames, startVideo, listVideos, getVideo, deleteVideo, newRequestId } from "./videoApi"

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" }) : "")
const border = { borderColor: "var(--dashboard-border)" }

// Saved Videos: running, ready and failed jobs. Polls while any job is still running, so closing the app loses nothing.
const SavedVideos = ({ refreshKey, onOpen, scriptId }) => {
  const [videos, setVideos] = useState(null)
  const load = useCallback(() => listVideos(scriptId).then(setVideos).catch(() => setVideos([])), [scriptId])
  useEffect(() => { load() }, [load, refreshKey])
  const running = (videos || []).some((v) => v.status === "processing")
  useEffect(() => { if (!running) return; const t = setInterval(load, 10000); return () => clearInterval(t) }, [running, load])
  if (!videos) return <p className="dashboard-muted text-[14px]">Loading saved videos…</p>
  return (
    <section aria-label="Saved Videos" className="flex flex-col gap-2">
      <h4 className="font-bold text-[16px]">Saved Videos</h4>
      {!videos.length && <p className="dashboard-muted text-[14px]">Your generated videos will appear here.</p>}
      {videos.map((v) => (
        <button key={v.id} type="button" onClick={() => onOpen(v.id)} disabled={v.status !== "ready"} className="saved-video flex items-center gap-3 rounded-xl border p-2 text-left bg-white disabled:cursor-default" style={border}>
          <div className="w-[96px] h-[54px] shrink-0 rounded-md overflow-hidden flex items-center justify-center" style={{ background: "var(--dashboard-tint)" }}>
            {v.thumbnail ? <img src={v.thumbnail} alt="" className="w-full h-full object-contain" /> : <MdPlayCircle size={24} color="var(--dashboard-primary)" />}
          </div>
          <div className="min-w-0 flex-1 text-[13px]">
            <div className="font-bold text-[14px] truncate">{v.scriptTitle}{v.sceneNumber ? ` · Scene ${v.sceneNumber}` : ""}</div>
            <div className="dashboard-muted truncate">{v.sceneHeading}</div>
            <div className="dashboard-muted">{v.duration}s · {fmtDate(v.createdAt)}{v.status === "ready" && v.expiresAt ? ` · Available until ${fmtDate(v.expiresAt)}` : ""}</div>
            {v.status === "processing" && <div role="status" style={{ color: "var(--dashboard-primary)" }} className="font-bold">Generating… you can close the app and come back.</div>}
            {v.status === "failed" && <div className="text-red-600">{v.error}</div>}
            {v.expired && <div className="dashboard-muted">This video has expired.</div>}
          </div>
          {v.status === "ready" && <MdChevronRight size={22} className="dashboard-muted shrink-0" />}
        </button>
      ))}
    </section>
  )
}

const VideoPlayer = ({ video, onBack, onDeleted }) => {
  const [busy, setBusy] = useState(false); const [err, setErr] = useState("")
  const [downloading, setDownloading] = useState(false)
  const download = async () => { setDownloading(true); setErr(""); try { downloadMedia((await getVideo(video.id)).downloadUrl) } catch (e) { setErr(e.message) } finally { setDownloading(false) } }
  const remove = async () => { setBusy(true); setErr(""); try { await deleteVideo(video.id); onDeleted() } catch (e) { setErr(e.message); setBusy(false) } }
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="dashboard-muted text-[14px] min-w-0 truncate">AI Video · {video.scriptTitle}{video.sceneNumber ? ` · Scene ${video.sceneNumber}` : ""} · {video.duration}s</p>
        <Button className="dashboard-tool rounded-full font-bold" icon={<MdArrowBack size={18} />} onClick={onBack}>Back</Button>
      </div>
      {video.playbackUrl ? <RenewableVideo resourceId={video.id} src={video.playbackUrl} renew={() => getVideo(video.id).then((v) => v.playbackUrl)} controls playsInline poster={video.thumbnail || undefined} className={`w-full rounded-xl bg-black ${videoClass(video)}`} /> : <p className="dashboard-muted">This video is no longer available.</p>}
      <div className="text-[14px]">
        <div className="font-bold">{video.sceneHeading}</div>
        {video.movement && <div><b>Movement:</b> {video.movement}</div>}
        {video.camera && <div><b>Camera:</b> {video.camera}</div>}
        {video.expiresAt && <div className="dashboard-muted text-[13px] mt-1">Available until {fmtDate(video.expiresAt)}</div>}
      </div>
      <div className="flex flex-wrap gap-3">
        {video.downloadUrl && <Button className="dashboard-primary rounded-full font-bold" icon={<MdDownload size={18} />} loading={downloading} onClick={download}>Download MP4</Button>}
        <Popconfirm title="Delete this video?" description="The video file is permanently deleted." okText="Delete" okButtonProps={{ danger: true }} onConfirm={remove}>
          <Button danger className="rounded-full font-bold" icon={<MdDelete size={18} />} loading={busy}>Delete video</Button>
        </Popconfirm>
      </div>
      {err && <p role="alert" className="text-red-600 text-[14px]">{err}</p>}
    </div>
  )
}

// Setup: script → scene → saved storyboard frame → length → optional movement/camera → Generate.
const StandardVideo = ({ scripts, scriptsLoading, fixedScript, beforeGenerate }) => {
  const [access, setAccess] = useState(null)
  const [scriptId, setScriptId] = useState(fixedScript?._id)
  const [frames, setFrames] = useState([]); const [framesLoading, setFramesLoading] = useState(false); const [frameKey, setFrameKey] = useState()
  const [aspectRatio, setAspectRatio] = useState("16:9")
  const [duration, setDuration] = useState(5); const [movement, setMovement] = useState(""); const [camera, setCamera] = useState("")
  const [busy, setBusy] = useState(false); const [err, setErr] = useState(""); const [notice, setNotice] = useState("")
  const [refreshKey, setRefreshKey] = useState(0); const [open, setOpen] = useState(null)
  const requestId = useRef(newRequestId())

  useEffect(() => { getVideoAccess().then(setAccess).catch(() => setAccess({ allowed: false, extraCredits: 0 })) }, [])
  useEffect(() => {
    setFrames([]); setFrameKey(undefined)
    if (!scriptId) return
    let cancelled = false
    setFramesLoading(true)
    listVideoFrames(scriptId).then(data => { if (!cancelled) setFrames(data) }).catch(() => { if (!cancelled) setFrames([]) }).finally(() => { if (!cancelled) setFramesLoading(false) })
    return () => { cancelled = true }
  }, [scriptId])
  // Any change to the request gets a new id; an unchanged repeat keeps the same one (no duplicate job).
  useEffect(() => { requestId.current = newRequestId() }, [scriptId, frameKey, duration, movement, camera, aspectRatio])

  const cost = videoDurations.find((d) => d.seconds === duration)?.credits
  const frame = frames.find((f) => `${f.storyboardId}:${f.frame}` === frameKey)
  const sourceRatio = frame && (storyboardRatio(frame) === "9:16" ? "9:16" : "16:9")
  const ready = (!frame || sourceRatio === aspectRatio) && access?.allowed && (access?.adminTest || access?.extraCredits >= cost) && scriptId && frame && !busy

  const generate = async () => {
    if (!ready) return
    setBusy(true); setErr(""); setNotice("")
    try {
      if (beforeGenerate) await beforeGenerate(scriptId)
      await startVideo({ scriptId, sceneId: frame.sceneId, storyboardId: frame.storyboardId, frame: frame.frame, duration, aspectRatio, movement: movement.trim(), camera: camera.trim(), requestId: requestId.current })
      setNotice("Your video is generating in the background. It appears in Saved Videos below — you can close the app and come back.")
      setRefreshKey((k) => k + 1)
    } catch (e) { setErr(e.message) } finally { setBusy(false) }
  }
  const openVideo = async (id) => { setErr(""); try { setOpen(await getVideo(id)) } catch (e) { setErr(e.message) } }

  if (open) return <VideoPlayer video={open} onBack={() => { setOpen(null); setRefreshKey((k) => k + 1) }} onDeleted={() => { setOpen(null); setRefreshKey((k) => k + 1) }} />

  return (
    <div className="flex flex-col gap-5">
      <div>
        <h3 className="font-bold text-[18px]">AI Video<span className="dashboard-muted font-normal text-[14px]"> · {videoDurations.map((d) => `${d.seconds}s ${d.credits} credits`).join(" · ")}</span></h3>
        <p className="dashboard-muted text-[14px] mt-1">Animate a saved storyboard frame into a short cinematic video (720p, no sound).</p>
        <p className="text-[13px] mt-2 rounded-lg px-3 py-2" style={{ background: "var(--dashboard-tint)" }}>AI credit balance: {access?.extraCredits ?? 0}</p>
        {access && !access.allowed && <p className="text-[13px] mt-2 font-bold">AI Video is in testing. It will open to everyone when Extra AI Credits can be purchased.</p>}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="min-w-0">
          <label className="block font-bold text-[14px] mb-2">Choose script</label>
          <Select aria-label="Choose script" size="large" className="w-full" placeholder="Choose script" showSearch optionFilterProp="label" loading={scriptsLoading} disabled={!!fixedScript} value={scriptId} onChange={setScriptId}
            options={scripts.map((s) => ({ value: s._id, label: s.title || "Untitled Script" }))} />
        </div>
      </div>
      {scriptId && <div>
        <div className="font-bold text-[14px] mb-2">Choose storyboard frame</div>
        {framesLoading ? <p className="dashboard-muted text-[14px]">Loading saved frames…</p>
          : !frames.length ? <p className="dashboard-muted text-[14px]">No saved storyboard frames for this screenplay yet. Generate a Storyboard first.</p>
          : <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">{frames.map((f) => { const k = `${f.storyboardId}:${f.frame}`; const on = k === frameKey; return (
            <button key={k} type="button" aria-pressed={on} onClick={() => { setFrameKey(k); setAspectRatio(storyboardRatio(f) === "9:16" ? "9:16" : "16:9") }} className="rounded-lg overflow-hidden border-2 text-left bg-white" style={{ borderColor: on ? "var(--dashboard-primary)" : "var(--dashboard-border)" }}>
              <img src={f.src} alt={`Frame ${f.frame + 1}: ${f.shot}`} className={`w-full ${mediaAspectClass(storyboardRatio(f))} object-contain`} />
              <div className="px-2 py-1 text-[12px] truncate"><b>Frame {f.frame + 1}</b> · {f.shot} · Original Scene {f.sceneNumber || f.sceneId}: {f.sceneHeading}</div>
            </button>) })}</div>}
      </div>}
      <AspectRatioSelect value={aspectRatio} onChange={setAspectRatio} />
      {frame && sourceRatio !== aspectRatio && <p role="status" className="text-[13px]">Choose a matching storyboard frame, or regenerate a new storyboard in this orientation first.</p>}
      <div>
        <div className="font-bold text-[14px] mb-2">Length</div>
        <div className="flex flex-wrap gap-2">{videoDurations.map((d) => (
          <button key={d.seconds} type="button" aria-pressed={duration === d.seconds} onClick={() => setDuration(d.seconds)} className="ai-chip rounded-full px-4 h-10 text-[14px]">{d.seconds} seconds · {d.credits} credits</button>))}</div>
      </div>
      <div>
        <label htmlFor="video-movement" className="block font-bold text-[14px] mb-2">Character movement (optional)</label>
        <Input.TextArea id="video-movement" rows={2} maxLength={1000} value={movement} onChange={(e) => setMovement(e.target.value)} placeholder="ANGEL turns from the window and walks towards JIMMY." />
      </div>
      <div>
        <label htmlFor="video-camera" className="block font-bold text-[14px] mb-2">Camera direction (optional)</label>
        <Input id="video-camera" size="large" maxLength={1000} value={camera} onChange={(e) => setCamera(e.target.value)} placeholder="Slow push in" />
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <Button className="dashboard-primary h-11 rounded-full px-5 font-bold" disabled={!ready} loading={busy} onClick={generate}>{`Generate video · ${cost} credits`}</Button>
      </div>
      {notice && <p role="status" className="text-[14px] -mt-2" style={{ color: "var(--dashboard-primary)" }}>{notice}</p>}
      {err && <p role="alert" className="text-red-600 text-[14px] -mt-2">{err}</p>}
      {access?.adminTest && <p className="dashboard-muted text-[12px] -mt-2">Credits are not deducted during Admin testing.</p>}
      <SavedVideos key={scriptId || "all"} scriptId={scriptId} refreshKey={refreshKey} onOpen={openVideo} />
    </div>
  )
}

// AI Video is the storyboard-based 5s/10s tool. Premium Video has its own entry in the AI Workspace menu.
export default StandardVideo
